import { useEffect, useMemo, useState, type MouseEvent } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { ConfirmationDialog } from "../../components/ConfirmationDialog";
import { ResetSubmissionIcon } from "../../components/Icons/ResetSubmissionIcon";
import { useAssessmentSession } from "../../contexts/assessmentUserContext";
import { useAvailableAssessmentBenchmarks } from "../../hooks/useAvailableAssessmentBenchmarks";
import { useDistrictsFilter } from "../../hooks/useDistrictsFilter";
import { useSchoolsFilter } from "../../hooks/useSchoolsFilter";
import {
  deleteSubmission,
  fetchSubmissions,
  resetSubmission,
  type SubmissionMeta,
} from "../../services/submissionService";

const ALL_FILTER_VALUE = "all";
const SUBMISSION_STATUS_COMPLETED = "completed";
const SUBMISSION_STATUS_IN_PROGRESS = "in_progress";

type FilterOption = {
  id: string;
  name?: string;
  districtId?: string;
};

type DistrictFilterOption = FilterOption;

const formatDate = (value?: string): string => {
  if (!value) {
    return "-";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString();
};

const formatScore = (submission: SubmissionMeta): string =>
  `${submission.correct_answer_count ?? 0}/${submission.assigned_question_count ?? 0}`;

const escapeCsv = (value: string): string => {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replaceAll('"', '""')}"`;
  }
  return value;
};

function optionDisplayName(name: string | undefined, id: string): string {
  return name?.trim() || id;
}

function renderSelectedOptionNames(
  selectedIds: string[],
  options: FilterOption[],
  emptyLabel: string,
): string {
  if (selectedIds.length === 0) {
    return emptyLabel;
  }

  const nameById = new Map<string, string | undefined>();
  options.forEach((option) => {
    nameById.set(option.id, option.name);
    if (option.districtId) {
      nameById.set(option.districtId, option.name);
    }
  });
  return selectedIds
    .map((id) => optionDisplayName(nameById.get(id), id))
    .join(", ");
}

export function SubmissionsDashboardPage() {
  const { token, me, isActualSuperAdmin } = useAssessmentSession();
  const [benchmarkId, setBenchmarkId] = useState<string>(ALL_FILTER_VALUE);
  const { benchmarks, benchmarkError } = useAvailableAssessmentBenchmarks();
  const benchmarkOptions = useMemo(
    () =>
      [...benchmarks].sort((left, right) =>
        left.title.localeCompare(right.title),
      ),
    [benchmarks],
  );
  const [testType, setTestType] = useState<string>(ALL_FILTER_VALUE);
  const [selectedDistrictIds, setSelectedDistrictIds] = useState<string[]>([]);
  const [selectedSchoolIds, setSelectedSchoolIds] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [submissions, setSubmissions] = useState<SubmissionMeta[]>([]);
  const [totalSubmissions, setTotalSubmissions] = useState(0);
  const [downloadMenuAnchorEl, setDownloadMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [deletingSubmissionId, setDeletingSubmissionId] = useState<
    string | null
  >(null);
  const [submissionIdToDelete, setSubmissionIdToDelete] = useState<
    string | null
  >(null);
  const [resettingSubmissionId, setResettingSubmissionId] = useState<
    string | null
  >(null);
  const [submissionIdToReset, setSubmissionIdToReset] = useState<string | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [assessmentMode, setAssessmentMode] =
    useState<string>(ALL_FILTER_VALUE);

  const { districts, isLoading: isLoadingDistricts } = useDistrictsFilter();
  const adminDistrictId = me?.district_id?.trim() || "";
  const activeDistrictIds = useMemo(
    () =>
      isActualSuperAdmin
        ? selectedDistrictIds
        : adminDistrictId
          ? [adminDistrictId]
          : [],
    [adminDistrictId, isActualSuperAdmin, selectedDistrictIds],
  );
  const districtIdsForSchools = useMemo(() => {
    if (activeDistrictIds.length > 0) {
      return activeDistrictIds;
    }
    if (!isActualSuperAdmin) {
      return [];
    }
    return districts.map((district) => district.id);
  }, [activeDistrictIds, districts, isActualSuperAdmin]);
  const { schools, isLoading: isLoadingSchools } = useSchoolsFilter({
    districtIds: districtIdsForSchools,
  });

  const effectiveDistrictId = isActualSuperAdmin
    ? ALL_FILTER_VALUE
    : adminDistrictId;

  const districtNameById = useMemo(() => {
    const next = new Map<string, string>();
    districts.forEach((district) => {
      const name = district.name || "";
      next.set(district.id, name);
      if (district.district_id) {
        next.set(district.district_id, name);
      }
    });
    return next;
  }, [districts]);

  const districtOptions = useMemo<DistrictFilterOption[]>(
    () =>
      districts.map((district) => ({
        id: district.id,
        name: district.name,
        districtId: district.district_id,
      })),
    [districts],
  );

  const schoolOptions = useMemo(() => {
    return schools
      .map((school) => ({
        id: school.id,
        name: school.name,
      }))
      .sort((left, right) =>
        optionDisplayName(left.name, left.id).localeCompare(
          optionDisplayName(right.name, right.id),
        ),
      );
  }, [schools]);

  const schoolNameById = useMemo(() => {
    const next = new Map<string, string>();
    schools.forEach((school) => {
      const name = school.name || "";
      next.set(school.id, name);
    });
    return next;
  }, [schools]);

  const getDistrictName = (submission: SubmissionMeta): string => {
    const fromApi = submission.district_name?.trim();
    if (fromApi) {
      return fromApi;
    }
    const districtId = submission.district_id?.trim();
    if (!districtId) {
      return "";
    }
    return optionDisplayName(districtNameById.get(districtId), districtId);
  };

  const getSchoolName = (submission: SubmissionMeta): string => {
    const fromApi = submission.school_name?.trim();
    if (fromApi) {
      return fromApi;
    }
    const schoolId = submission.school_id?.trim();
    if (!schoolId) {
      return "";
    }
    return optionDisplayName(schoolNameById.get(schoolId), schoolId);
  };

  useEffect(() => {
    const controller = new AbortController();

    const loadSubmissions = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const payload = await fetchSubmissions({
          token,
          isSuperAdmin: isActualSuperAdmin,
          districtIds: activeDistrictIds,
          schoolIds: selectedSchoolIds,
          benchmarkId:
            benchmarkId === ALL_FILTER_VALUE ? undefined : benchmarkId,
          testType: testType === ALL_FILTER_VALUE ? undefined : testType,
          page,
          limit: rowsPerPage,
          signal: controller.signal,
          assessmentMode:
            assessmentMode === ALL_FILTER_VALUE ? undefined : assessmentMode,
        });

        if (controller.signal.aborted) {
          return;
        }
        const nextSubmissions = payload.submissions ?? [];
        setSubmissions(nextSubmissions);
        setTotalSubmissions(payload.total ?? nextSubmissions.length);
      } catch (requestError: unknown) {
        if (
          controller.signal.aborted ||
          (requestError instanceof DOMException &&
            requestError.name === "AbortError")
        ) {
          return;
        }
        setError(
          requestError instanceof Error
            ? requestError.message
            : "Unable to load submissions.",
        );
        setSubmissions([]);
        setTotalSubmissions(0);
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    loadSubmissions().then();

    return () => {
      controller.abort();
    };
  }, [
    activeDistrictIds,
    benchmarkId,
    effectiveDistrictId,
    isActualSuperAdmin,
    page,
    rowsPerPage,
    selectedSchoolIds,
    testType,
    token,
    assessmentMode,
  ]);

  const buildCsvContent = (rowsToExport: SubmissionMeta[]) => {
    const rows = [
      [
        "user_id",
        "first_name",
        "last_name",
        "submission_id",
        "district_id",
        "district_name",
        "school_id",
        "school_name",
        "grade",
        "test_type",
        "status",
        "created_at",
        "correct_answer_count",
        "assigned_question_count",
        "oral_reading_submission_count",
        "oral_reading_audio_1_url",
        "oral_reading_audio_2_url",
        "oral_reading_audio_3_url",
        "recap_audio_url",
      ],
      ...rowsToExport.map((submission) => [
        submission.user_id ?? "",
        submission.first_name ?? "",
        submission.last_name ?? "",
        submission.submission_id ?? "",
        submission.district_id ?? "",
        getDistrictName(submission),
        submission.school_id ?? "",
        getSchoolName(submission),
        submission.grade ?? "",
        submission.test_type ?? "",
        submission.status ?? "",
        submission.created_at ?? "",
        String(submission.correct_answer_count ?? 0),
        String(submission.assigned_question_count ?? 0),
        String(submission.oral_reading_submission_count ?? 0),
        submission.oral_reading_audio_1_url ?? "",
        submission.oral_reading_audio_2_url ?? "",
        submission.oral_reading_audio_3_url ?? "",
        submission.recap_audio_url ?? "",
      ]),
    ];

    return rows
      .map((row) => row.map((column) => escapeCsv(column)).join(","))
      .join("\n");
  };

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      setError(null);
      const payload = await fetchSubmissions({
        token,
        isSuperAdmin: isActualSuperAdmin,
        districtIds: activeDistrictIds,
        schoolIds: selectedSchoolIds,
        benchmarkId: benchmarkId === ALL_FILTER_VALUE ? undefined : benchmarkId,
        testType: testType === ALL_FILTER_VALUE ? undefined : testType,
        assessmentMode:
          assessmentMode === ALL_FILTER_VALUE ? undefined : assessmentMode,
      });
      const csvContent = buildCsvContent(payload.submissions ?? []);
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `assessment-submissions-${new Date().toISOString()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (requestError: unknown) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "Unable to download submissions.",
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadMenuOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setDownloadMenuAnchorEl(event.currentTarget);
  };

  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchorEl(null);
  };

  const handleDistrictsChange = (
    event: SelectChangeEvent<typeof selectedDistrictIds>,
  ) => {
    const value = event.target.value;
    setSelectedDistrictIds(
      typeof value === "string" ? value.split(",").filter(Boolean) : value,
    );
    setSelectedSchoolIds([]);
    setPage(0);
  };

  const handleSchoolsChange = (
    event: SelectChangeEvent<typeof selectedSchoolIds>,
  ) => {
    const value = event.target.value;
    setSelectedSchoolIds(
      typeof value === "string" ? value.split(",").filter(Boolean) : value,
    );
    setPage(0);
  };

  const handleTestTypeChange = (event: SelectChangeEvent<string>) => {
    setTestType(event.target.value);
    setPage(0);
  };

  const handleAssessmentModeChange = (event: SelectChangeEvent<string>) => {
    setAssessmentMode(event.target.value);
    setPage(0);
  };

  const handleDeleteSubmission = async (submissionId: string) => {
    if (!isActualSuperAdmin) {
      return;
    }

    try {
      setDeletingSubmissionId(submissionId);
      setError(null);
      await deleteSubmission({ token, submissionId });

      setSubmissions((current) =>
        current.filter(
          (submission) => submission.submission_id !== submissionId,
        ),
      );
      setTotalSubmissions((current) => Math.max(0, current - 1));
      setSubmissionIdToDelete(null);
    } catch (requestError: unknown) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "Unable to delete submission.",
      );
    } finally {
      setDeletingSubmissionId(null);
    }
  };

  const handleResetSubmission = async (submissionId: string) => {
    if (!isActualSuperAdmin) {
      return;
    }

    try {
      setResettingSubmissionId(submissionId);
      setError(null);
      await resetSubmission({ token, submissionId });

      setSubmissions((current) =>
        current.map((submission) =>
          submission.submission_id === submissionId
            ? { ...submission, status: SUBMISSION_STATUS_IN_PROGRESS }
            : submission,
        ),
      );
      setSubmissionIdToReset(null);
    } catch (requestError: unknown) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "Unable to reset submission.",
      );
    } finally {
      setResettingSubmissionId(null);
    }
  };

  const actionColumnCount = isActualSuperAdmin ? 1 : 0;
  const tableColumnCount = 9 + actionColumnCount;

  return (
    <Container
      maxWidth="lg"
      sx={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          position: "relative",
          flexShrink: 0,
          pt: 2,
          pb: 1.5,
        }}
      >
        <Typography variant="h4" component="h1">
          Submissions
        </Typography>
        <>
          <IconButton
            aria-label="More download options"
            onClick={handleDownloadMenuOpen}
            disabled={totalSubmissions === 0 || isLoading || isDownloading}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={downloadMenuAnchorEl}
            open={Boolean(downloadMenuAnchorEl)}
            onClose={handleDownloadMenuClose}
          >
            <MenuItem
              onClick={() => {
                handleDownload();
                handleDownloadMenuClose();
              }}
            >
              {isDownloading ? "Downloading..." : "Download filtered CSV"}
            </MenuItem>
          </Menu>
        </>
      </Stack>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pb: 4 }}>
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            useFlexGap
            sx={{ flexWrap: "wrap" }}
          >
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <Select
                inputProps={{ "aria-label": "District filter" }}
                multiple
                displayEmpty
                value={activeDistrictIds}
                disabled={!isActualSuperAdmin || isLoadingDistricts}
                onChange={handleDistrictsChange}
                renderValue={(selected) =>
                  renderSelectedOptionNames(
                    selected,
                    districtOptions,
                    "All districts",
                  )
                }
              >
                {!isActualSuperAdmin && effectiveDistrictId ? (
                  <MenuItem value={effectiveDistrictId}>
                    <Checkbox
                      checked={activeDistrictIds.includes(effectiveDistrictId)}
                    />
                    <ListItemText
                      primary={optionDisplayName(
                        districtNameById.get(effectiveDistrictId),
                        effectiveDistrictId,
                      )}
                    />
                  </MenuItem>
                ) : null}
                {districtOptions
                  .filter(
                    (district) =>
                      isActualSuperAdmin || district.id !== effectiveDistrictId,
                  )
                  .map((district) => (
                    <MenuItem key={district.id} value={district.id}>
                      <Checkbox
                        checked={activeDistrictIds.includes(district.id)}
                      />
                      <ListItemText
                        primary={optionDisplayName(district.name, district.id)}
                      />
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <Select
                inputProps={{ "aria-label": "School filter" }}
                multiple
                displayEmpty
                value={selectedSchoolIds}
                disabled={
                  isLoadingSchools ||
                  (activeDistrictIds.length > 0 &&
                    districtIdsForSchools.length === 0)
                }
                onChange={handleSchoolsChange}
                renderValue={(selected) =>
                  renderSelectedOptionNames(
                    selected,
                    schoolOptions,
                    "All schools",
                  )
                }
              >
                {schoolOptions.map((school) => (
                  <MenuItem key={school.id} value={school.id}>
                    <Checkbox checked={selectedSchoolIds.includes(school.id)} />
                    <ListItemText
                      primary={optionDisplayName(school.name, school.id)}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 220, maxWidth: "100%" }}>
              <InputLabel id="submission-benchmark-filter-label">
                Benchmark
              </InputLabel>
              <Select
                labelId="submission-benchmark-filter-label"
                value={benchmarkId}
                label="Benchmark"
                onChange={(event) => {
                  setBenchmarkId(event.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value={ALL_FILTER_VALUE}>All benchmarks</MenuItem>
                {benchmarkOptions.map((benchmark) => (
                  <MenuItem key={benchmark.id} value={benchmark.id}>
                    {optionDisplayName(benchmark.title, benchmark.id)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="submission-test-type-filter-label">
                Test Type
              </InputLabel>
              <Select
                labelId="submission-test-type-filter-label"
                value={testType}
                label="Test Type"
                onChange={handleTestTypeChange}
              >
                <MenuItem value={ALL_FILTER_VALUE}>All tests</MenuItem>
                <MenuItem value="reading">Reading</MenuItem>
                <MenuItem value="listening">Listening</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="submission-assessment-model-filter-label">
                Assessment Mode
              </InputLabel>
              <Select
                labelId="submission-assessment-model-filter-label"
                value={assessmentMode}
                label="Assessment Mode"
                onChange={handleAssessmentModeChange}
              >
                <MenuItem value={ALL_FILTER_VALUE}>All</MenuItem>
                <MenuItem value="benchmarks">Benchmarks</MenuItem>
                <MenuItem value="activities">Activities</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Showing {submissions.length} of {totalSubmissions} submissions
          </Typography>
        </Paper>
        {isLoading ? (
          <Typography variant="body1" sx={{ mb: 2 }}>
            Loading submissions...
          </Typography>
        ) : null}
        {benchmarkError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {benchmarkError}
          </Alert>
        ) : null}
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        {!isLoading && (!error || submissions.length > 0) ? (
          <Paper variant="outlined">
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Student Name</TableCell>
                    <TableCell>Submission ID</TableCell>
                    <TableCell>District</TableCell>
                    <TableCell>School</TableCell>
                    <TableCell>Grade</TableCell>
                    <TableCell>Test Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Score</TableCell>
                    <TableCell>Created At</TableCell>
                    {isActualSuperAdmin ? (
                      <TableCell align="center">Actions</TableCell>
                    ) : null}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {submissions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={tableColumnCount}>
                        <Box sx={{ py: 2 }}>
                          <Typography color="text.secondary">
                            No submissions found.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    submissions.map((submission) => (
                      <TableRow
                        key={`${submission.user_id}-${submission.submission_id}`}
                      >
                        <TableCell>
                          {`${submission.first_name ?? ""} ${submission.last_name ?? ""}`.trim() ||
                            "-"}
                        </TableCell>
                        <TableCell>{submission.submission_id || "-"}</TableCell>
                        <TableCell>
                          {getDistrictName(submission) || "-"}
                        </TableCell>
                        <TableCell>
                          {getSchoolName(submission) || "-"}
                        </TableCell>
                        <TableCell>{submission.grade || "-"}</TableCell>
                        <TableCell>{submission.test_type || "-"}</TableCell>
                        <TableCell>{submission.status || "-"}</TableCell>
                        <TableCell>{formatScore(submission)}</TableCell>
                        <TableCell>
                          {formatDate(submission.created_at)}
                        </TableCell>
                        {isActualSuperAdmin ? (
                          <TableCell
                            align="center"
                            sx={{ whiteSpace: "nowrap" }}
                          >
                            <Tooltip
                              title={
                                submission.status ===
                                SUBMISSION_STATUS_COMPLETED
                                  ? "Reset submission"
                                  : "Only completed submissions can be reset"
                              }
                            >
                              <span>
                                <IconButton
                                  aria-label="Reset submission"
                                  size="small"
                                  onClick={() =>
                                    setSubmissionIdToReset(
                                      submission.submission_id,
                                    )
                                  }
                                  disabled={
                                    submission.status !==
                                      SUBMISSION_STATUS_COMPLETED ||
                                    resettingSubmissionId ===
                                      submission.submission_id
                                  }
                                >
                                  <ResetSubmissionIcon
                                    fontSize="small"
                                    stroke={
                                      submission.status ===
                                      SUBMISSION_STATUS_COMPLETED
                                        ? undefined
                                        : "currentColor"
                                    }
                                  />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Delete submission">
                              <span>
                                <Button
                                  aria-label="Delete submission"
                                  color="error"
                                  size="small"
                                  variant="text"
                                  onClick={() =>
                                    setSubmissionIdToDelete(
                                      submission.submission_id,
                                    )
                                  }
                                  disabled={
                                    deletingSubmissionId ===
                                    submission.submission_id
                                  }
                                >
                                  {deletingSubmissionId ===
                                  submission.submission_id ? (
                                    "Deleting..."
                                  ) : (
                                    <DeleteIcon />
                                  )}
                                </Button>
                              </span>
                            </Tooltip>
                          </TableCell>
                        ) : null}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalSubmissions}
              page={page}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              onPageChange={(_event, nextPage) => setPage(nextPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(Number(event.target.value));
                setPage(0);
              }}
            />
          </Paper>
        ) : null}
      </Box>
      <ConfirmationDialog
        open={Boolean(submissionIdToDelete)}
        title="Delete submission?"
        description={`Delete submission "${submissionIdToDelete ?? ""}" and all associated answers/events?`}
        confirmLabel="Delete Submission"
        confirmColor="error"
        isConfirming={Boolean(
          submissionIdToDelete && deletingSubmissionId === submissionIdToDelete,
        )}
        onClose={() => {
          if (!deletingSubmissionId) {
            setSubmissionIdToDelete(null);
          }
        }}
        onConfirm={() => {
          if (!submissionIdToDelete) {
            return;
          }
          return handleDeleteSubmission(submissionIdToDelete);
        }}
      />
      <ConfirmationDialog
        open={Boolean(submissionIdToReset)}
        title="Reset submission?"
        description={`Reset submission "${submissionIdToReset ?? ""}" to in progress? The recap step will need to be completed again and its recap audio will be removed.`}
        confirmLabel="Reset Submission"
        isConfirming={Boolean(
          submissionIdToReset && resettingSubmissionId === submissionIdToReset,
        )}
        onClose={() => {
          if (!resettingSubmissionId) {
            setSubmissionIdToReset(null);
          }
        }}
        onConfirm={() => {
          if (!submissionIdToReset) {
            return;
          }
          return handleResetSubmission(submissionIdToReset);
        }}
      />
    </Container>
  );
}
